import { collection, getDocs, query, where, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { Customer, CustomerRankStats, ID } from '@shared/types';
import { buildUpdateFields, writeAuditLog } from '@shared/utils';
import { computeCustomerRank } from '../utils/customer-rank';

const INV_COL = 'invoices';
const CUST_COL = 'customers';
// 確定とみなす請求ステータス（下書き・キャンセルは集計から除外）。
const CONFIRMED_STATUSES = new Set(['issued', 'partially_paid', 'paid', 'overdue']);
const BATCH_LIMIT = 400; // Firestore の1バッチ上限(500)に対する安全マージン

function emptyStats(): CustomerRankStats {
  return { lifetimeInvoiced: 0, lifetimeOrderCount: 0, rolling12mInvoiced: 0 };
}

/** 全顧客の確定請求から、累計・直近12ヶ月の実績を顧客IDごとに集計する。 */
async function aggregateStats(cutoff: Timestamp): Promise<Map<string, CustomerRankStats>> {
  const snap = await getDocs(query(collection(db, INV_COL), where('isDeleted', '==', false)));
  const map = new Map<string, CustomerRankStats>();
  snap.forEach((d) => {
    const data = d.data();
    if (!CONFIRMED_STATUSES.has(data.status as string)) return;
    const customerId = data.customerId as string;
    if (!customerId) return;
    const total = (data.total as number) ?? 0;
    const stats = map.get(customerId) ?? emptyStats();
    stats.lifetimeInvoiced += total;
    stats.lifetimeOrderCount += 1;
    const issued = data.issueDate as Timestamp | undefined;
    if (issued && issued.toMillis() >= cutoff.toMillis()) {
      stats.rolling12mInvoiced += total;
    }
    map.set(customerId, stats);
  });
  return map;
}

function statsChanged(prev: CustomerRankStats | null, next: CustomerRankStats): boolean {
  return (
    !prev ||
    prev.lifetimeInvoiced !== next.lifetimeInvoiced ||
    prev.lifetimeOrderCount !== next.lifetimeOrderCount ||
    prev.rolling12mInvoiced !== next.rolling12mInvoiced
  );
}

export interface RankRecomputeResult {
  evaluated: number; // 自動算定の対象となった顧客数
  updated: number; // ランクまたは集計値が変わって更新した顧客数
}

/**
 * 全顧客のランクを取引実績から再計算し、変更があった顧客のみ更新する。
 * rankMode='manual' の顧客は対象外。月次締め処理から呼ばれる。
 * @param referenceDate 直近12ヶ月の起点（既定は現在日時）。
 */
export async function recomputeAllCustomerRanks(
  userId: ID,
  referenceDate: Date = new Date(),
): Promise<RankRecomputeResult> {
  const cutoff = Timestamp.fromDate(
    new Date(referenceDate.getFullYear() - 1, referenceDate.getMonth(), referenceDate.getDate()),
  );
  const [statsMap, custSnap] = await Promise.all([
    aggregateStats(cutoff),
    getDocs(query(collection(db, CUST_COL), where('isDeleted', '==', false))),
  ]);

  const now = Timestamp.now();
  let evaluated = 0;
  let updated = 0;
  let batch = writeBatch(db);
  let ops = 0;

  for (const d of custSnap.docs) {
    const c = d.data() as Customer;
    if (c.rankMode === 'manual') continue; // 手動固定は対象外
    evaluated += 1;
    const stats = statsMap.get(d.id) ?? emptyStats();
    const nextRank = computeCustomerRank(stats);
    if (c.rank === nextRank && !statsChanged(c.rankStats ?? null, stats)) continue;
    batch.update(d.ref, {
      rank: nextRank,
      rankStats: stats,
      rankUpdatedAt: now,
      ...buildUpdateFields(userId),
    });
    updated += 1;
    ops += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();

  await writeAuditLog({
    entityType: 'customer',
    entityId: 'rank_recompute',
    action: 'update',
    userId,
    after: { evaluated, updated, referenceDate: referenceDate.toISOString() },
  });

  return { evaluated, updated };
}
